// Pricing.js — journey-based pricing: Truth → Assurance → Programme → Protection.
// Price escalates with the value/risk a customer is actually buying at each
// stage, not with seat count. Entry point is a low-friction, one-off paid
// scan — deliberately cheap to say yes to, for a self-serve, multi-company
// motion — everything above it is a recurring subscription.
import React from "react";
import { motion } from "framer-motion";
import { Check, ChevronRight } from "lucide-react";

const PLANS = [
  {
    key: "truth",
    stage: "Stage 1 · Truth",
    name: "Data Quality Scan",
    blurb: "See what's really going on — one dataset, one honest answer.",
    price: "£49",
    period: "one-off",
    cta: "Get your score",
    highlight: false,
    features: [
      "One dataset — upload a file or connect a source",
      "Full automated profiling (completeness, validity, uniqueness, outliers)",
      "AI-generated data quality score & risk summary",
      "Duplicate detection within the dataset",
      "Exportable PDF report",
      "No subscription — pay once, keep the report",
    ],
  },
  {
    key: "assurance",
    stage: "Stage 2 · Assurance",
    name: "Assurance",
    blurb: "Keep that trust current, not just a one-time snapshot.",
    price: "£249",
    period: "/month",
    sub: "single dataset",
    cta: "Start Assurance",
    highlight: false,
    features: [
      "Everything in the Data Quality Scan",
      "Scheduled re-scans & trend tracking over time",
      "In-app AI assistant — ask questions about this dataset directly",
      "Full change log & audit trail",
      "Email support",
    ],
  },
  {
    key: "programme",
    stage: "Stage 3 · Programme",
    name: "Programme",
    blurb: "For teams who want daily visibility across every dataset, not just one.",
    price: "Custom",
    period: "pricing",
    sub: "scoped to your team",
    cta: "Talk to sales",
    highlight: true,
    features: [
      "Everything in Assurance",
      "Priced around your team, not a fixed seat count — migration teams, business users, or a mix",
      "Multiple datasets and multiple users",
      "Org-wide quality dashboard — every entity, every dimension, one view",
      "Scheduled profiling & trend analysis across all datasets",
      "AI-powered cleansing recommendations & custom rule management",
      "Connect your own Claude or another MCP-compatible AI agent",
      "AI-generated status reports, sent straight to stakeholders",
    ],
  },
  {
    key: "enterprise",
    stage: "Stage 4 · Protection",
    name: "Enterprise Protection",
    blurb: "Full migration programmes and day-to-day analysis, at organisation scale.",
    price: "Custom",
    period: "pricing",
    sub: "scoped to your organisation",
    cta: "Talk to sales",
    highlight: false,
    features: [
      "Everything in Programme",
      "Connect SQL Server, Oracle, SharePoint, and Dynamics 365 F&O",
      "Full source-to-target migration pipeline with a quality gate at every stage",
      "Shared connection library — set up once, reuse across every project",
      "Role-based access, per-entity governance, admin-gated AI actions",
      "Regulatory rule packs (GDPR, retention, PII)",
      "Continuous quality & compliance monitoring with automated alerts",
      "API access & enterprise integrations",
      "Host it yourself on your own Azure, or let us host it",
      "Dedicated support, SLA & onboarding",
    ],
  },
];

export default function Price({
  title = "Priced against the risk you're avoiding, not a seat count.",
  subtitle = "Start with a one-off scan for £49, or ongoing assurance from £249/month. Running a migration team, a mix of business users, or something bigger? Programme and Enterprise are scoped to your team and quoted directly — team shapes vary too much for one flat number.",
}) {
  return (
    <section id="pricing" className="relative py-20">
      {/* ambient wash */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute left-1/2 top-0 h-64 w-[85vw] -translate-x-1/2 rounded-[8rem] bg-[radial-gradient(40rem_16rem_at_50%_0%,rgba(96,165,250,0.16),transparent)]" />
        <div className="absolute inset-0 opacity-[0.035] bg-[linear-gradient(115deg,#000_1px,transparent_1px)] bg-[length:20px_20px]" />
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Heading */}
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
            {title}
          </h2>
          <p className="mt-3 text-slate-600">{subtitle}</p>
        </div>

        {/* Journey strip */}
        <div className="mx-auto mt-8 flex max-w-3xl flex-wrap items-center justify-center gap-2 text-xs font-medium text-slate-500">
          {["Truth", "Assurance", "Programme", "Protection"].map((s, i, arr) => (
            <React.Fragment key={s}>
              <span className="rounded-full border border-blue-200 bg-blue-50/60 px-3 py-1 text-blue-700">
                {s}
              </span>
              {i < arr.length - 1 && <ChevronRight className="h-3.5 w-3.5 text-slate-300" />}
            </React.Fragment>
          ))}
        </div>

        {/* Plans */}
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {PLANS.map((p, idx) => (
            <motion.article
              key={p.key}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.35 }}
              transition={{ duration: 0.5, delay: idx * 0.05 }}
              className={`relative flex w-full flex-col overflow-hidden rounded-[2rem] border p-6 backdrop-blur-md shadow-lg ${
                p.highlight
                  ? "border-blue-300 bg-white/85"
                  : "border-blue-100 bg-white/70"
              }`}
            >
              {/* subtle gradient wash */}
              <div
                className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${
                  p.highlight
                    ? "from-blue-50 via-white to-cyan-50"
                    : "from-white to-white"
                }`}
              />

              {p.highlight && (
                <span className="relative mb-3 inline-flex w-fit items-center rounded-full bg-gradient-to-r from-blue-600 to-cyan-400 px-3 py-1 text-[11px] font-semibold text-white shadow">
                  Most popular
                </span>
              )}

              {/* header */}
              <div className="relative">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-blue-600">
                  {p.stage}
                </p>
                <h3 className="mt-1 text-xl font-semibold text-slate-900">
                  {p.name}
                </h3>
                <p className="mt-1 text-sm text-slate-600">{p.blurb}</p>
                <div className="mt-5 flex items-end gap-2">
                  <div className="text-3xl font-semibold text-slate-900">
                    {p.price}
                  </div>
                  <div className="pb-1 text-sm text-slate-500">{p.period}</div>
                </div>
                {p.sub && <div className="text-xs text-slate-500">{p.sub}</div>}
              </div>

              {/* CTA */}
              <div className="relative mt-6">
                <a
                  href={
                    p.key === "enterprise" || p.key === "programme"
                      ? "mailto:sales@harlowdata.com"
                      : "https://app.prismdq.com/dashboard"
                  }
                  className={`inline-flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold shadow-md transition ${
                    p.highlight
                      ? "bg-gradient-to-r from-blue-600 via-sky-500 to-cyan-400 text-white hover:opacity-95"
                      : "bg-white/80 text-slate-800 ring-1 ring-slate-200 hover:bg-white"
                  }`}
                >
                  {p.cta}
                  <ChevronRight className="h-4 w-4" />
                </a>
              </div>

              {/* features */}
              <ul className="relative mt-6 space-y-3">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-3">
                    <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-blue-600/10 ring-1 ring-blue-200">
                      <Check className="h-3.5 w-3.5 text-blue-700" />
                    </span>
                    <span className="text-sm text-slate-700">{f}</span>
                  </li>
                ))}
              </ul>
            </motion.article>
          ))}
        </div>

        {/* Footnotes */}
        <div className="mt-8 grid grid-cols-1 items-center gap-4 text-center sm:grid-cols-3">
          <div className="text-xs text-slate-500">
            We charge for certainty, not curiosity — the scan tells you if you need more.
          </div>
          <div className="text-xs text-slate-500">
            Transparent pricing • switch or cancel any subscription any time
          </div>
          <div className="text-xs text-slate-500">
            Need a custom quote?{" "}
            <a
              href="mailto:sales@harlowdata.com"
              className="font-semibold text-blue-700 underline decoration-blue-200 underline-offset-4"
            >
              Talk to sales
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
